import { PermissionCode } from '@/entities/profile';
import { RouteConfig } from '@/shared/lib';
import { matchPath } from 'react-router-dom';

export function isActive(route: RouteConfig, pathname: string) {
  // If navbar.path is specified, use only it for matching (exact match or with children)
  if (route.navbar?.path) {
    const navbarPath = route.navbar.path.startsWith('/') ? route.navbar.path : `/${route.navbar.path}`;
    return pathname === navbarPath || pathname.startsWith(`${navbarPath}/`);
  }

  // If no navbar.path, use matchPath with route.path for dynamic routes
  if (route.path) {
    return !!matchPath(route.path, pathname);
  }

  return false;
}

export function isActiveNested(route: RouteConfig, pathname: string): boolean {
  if (!route.children?.length) {
    return isActive(route, pathname);
  }
  return !!route.children.some((child) => isActiveNested(child, pathname));
}

export function calcGroupName(rootName: string, indexOrSubIndex?: number): string {
  return [rootName, indexOrSubIndex?.toString()].filter(Boolean).join('.');
}

/**
 * Разворачивает дерево вложенных групп в плоский список навигируемых пунктов.
 * Видимость пунктов не проверяет — отбирает их вызывающий по правилу видимости пункта меню.
 */
export function collectLeafRoutes(children: readonly RouteConfig[] = []): RouteConfig[] {
  return children.flatMap((child) => {
    if (child.children?.length) {
      return collectLeafRoutes(child.children);
    }
    return child.navbar ? [child] : [];
  });
}

/**
 * Разрешения группы меню вместе с разрешениями всех её пунктов и вложенных групп — плоским
 * списком. Пункт без разрешения даёт `undefined`: группу он видимой не делает.
 */
export function collectGroupPermissions(route: RouteConfig): (PermissionCode | undefined)[] {
  if (route.children) {
    return [route.viewPermission, ...route.children.map(collectGroupPermissions)].flat();
  }
  return [route.viewPermission].flat();
}

/**
 * Адрес первого пункта меню, который видит пользователь, — в порядке меню и по тем же
 * правилам видимости группы и пункта, что и само меню. Динамические пункты (закреплённые
 * пресеты, активные интеграции) не рассматриваются: это подпункты раздела, а не разделы.
 * Нет видимого пункта — `undefined`.
 */
export function findFirstMenuPath(
  menuItems: readonly RouteConfig[],
  isRouteVisible: (route: RouteConfig) => boolean,
  isGroupVisible: (route: RouteConfig) => boolean,
): string | undefined {
  const findInGroup = (group: RouteConfig): string | undefined => {
    if (!isGroupVisible(group)) return undefined;

    for (const child of group.children ?? []) {
      const path = child.children?.length ? findInGroup(child) : findLeafPath(child);
      if (path) return path;
    }
    return undefined;
  };

  const findLeafPath = (route: RouteConfig): string | undefined => {
    const path = route.navbar?.path || route.path;
    if (!route.navbar || !path || !isRouteVisible(route)) return undefined;
    return path.startsWith('/') ? path : `/${path}`;
  };

  for (const route of menuItems) {
    // Тот же отбор и то же деление на группы и пункты, что у корня меню (AppSidebaItems)
    if (!route.navbar && route.type !== 'group') continue;

    const path = route.type === 'group' || !route.path ? findInGroup(route) : findLeafPath(route);
    if (path) return path;
  }
  return undefined;
}
